import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppData } from '../context/AppDataContext';
import { calculateTotalReadPages } from '../utils/bookUtils';
import { getPageScale } from '../utils/pageScale';
import {
  buildCertificationText,
  getCertTrack,
  getCertificationDay,
  isCertifiedToday
} from '../utils/certification';

// 인증 토스트는 한 번에 하나만 (같은 id 로 덮어쓴다)
const TOAST_ID = 'reading-cert';

/**
 * 단톡방에 붙여 넣는 독서 인증 한 줄.
 *
 * 쪽수는 화면에 보이는 단위(전자책이면 전자책 쪽)로 적는다 —
 * 사용자가 입력한 숫자와 진도 표시가 같은 척도라야 인증도 맞는다.
 *
 * @param {number} extraPages 아직 저장하지 않고 입력 중인 쪽수 (미리보기용)
 */
const ReadingCertification = ({ book, extraPages = 0, className = '' }) => {
  const { userSettings, today, certifyReading } = useAppData();
  const [copied, setCopied] = useState(false);

  const scale = getPageScale(book);
  const totalRead = scale.toDisplay(calculateTotalReadPages(book)) ?? 0;
  const todayRead = scale.toDisplay(book.readingHistory?.[today] || 0) ?? 0;

  // 입력 중이면 그 값으로 미리 보여주고, 아니면 오늘 기록해둔 쪽수로 만든다
  const draft = Number.isFinite(extraPages) && extraPages > 0 ? extraPages : 0;
  const pages = draft || todayRead;
  const startPage = draft ? totalRead : totalRead - todayRead;
  const endPage = startPage + pages;

  const track = getCertTrack(book);
  const day = getCertificationDay(userSettings, track, today);
  const alreadyToday = isCertifiedToday(userSettings, track, today);

  if (pages <= 0) return null;

  // 앞머리 이름은 buildCertificationText 가 필명으로 고정해 붙인다.
  // 설정의 '화면에 보이는 이름'을 바꿔도 인증 문구는 그대로여야 한다
  const text = buildCertificationText({
    day,
    title: book.title,
    startPage,
    endPage,
    minutes: pages * (book.minutesPerPage || 0)
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      toast.error('복사에 실패했어요. 텍스트를 직접 선택해 복사해주세요', { id: TOAST_ID });
      return;
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    // 복사가 곧 인증이다. 같은 날 두 번 눌러도 하루만 센다.
    // 토스트는 고정 id 로 하나만 띄운다 — 연달아 누를 때 여러 개가 쌓이지 않게.
    try {
      const result = await certifyReading(track);
      toast.success(
        result?.counted
          ? `복사했어요 · 누적 인증 ${result.day}일`
          : `복사했어요 · 오늘은 이미 ${result?.day ?? day}일로 인증했어요`,
        { id: TOAST_ID }
      );
    } catch {
      toast.error('복사는 했지만 누적 인증일수를 저장하지 못했어요', { id: TOAST_ID });
    }
  };

  return (
    <div className={`surface p-3 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="label">독서 인증</span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 text-xs text-ink-700 dark:text-ink-200 hover:text-ink-950 dark:hover:text-white transition-colors font-sans"
          title={alreadyToday ? '오늘은 이미 인증했어요 (다시 복사만 합니다)' : '복사하면 누적 인증일수가 1 올라갑니다'}
        >
          {/* 아이콘과 글자를 각각 span 으로 감싼다.
              바뀌는 요소(Copy↔Check)를 텍스트와 형제로 두면, 리액트가 새 아이콘을
              '텍스트 노드 앞에' 끼워 넣으려다 insertBefore 오류를 낸다.
              각자 제 칸의 유일한 자식이면 그냥 갈아 끼우면 되므로 그 경로가 사라진다. */}
          <span className="inline-flex w-3.5 shrink-0 justify-center">
            {copied
              ? <Check size={13} strokeWidth={1.5} />
              : <Copy size={13} strokeWidth={1.5} />}
          </span>
          <span>{copied ? '복사됨' : '복사하기'}</span>
        </button>
      </div>
      <p className="mt-1.5 text-sm text-ink-800 dark:text-ink-200 font-mono break-all">
        {text}
      </p>
    </div>
  );
};

export default ReadingCertification;
